import { useCallback, useEffect, useRef } from 'react';

import { AUTH_MODE, WAITING_CONFIRMATION_MODE } from '@src/components/Checkout/constants';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { buyerSelector } from '@src/features/customers';
import { currentOrderSelector, hasPendingSaleRequiredFormInputsSelector } from '@src/features/orders/store/order';
import {
  currentSaleChangeSelector,
  resetSaleChange,
  setShowAppliedFeedbackForSaleChange,
} from '@src/stores/saleChange';
import { currentSaleSelector } from '@src/stores/sale';

import type { Order } from '@src/features/orders/types';

import { CheckoutMode } from '../types';
import { decideCheckoutMode, decideCheckoutModeAfterAuth, type CheckoutModeDecision } from '../utils';

type UseCheckoutModeDecisionParams = {
  mode: CheckoutMode;
  /** Automatic step change — replaces the current entry under routing, so back never bounces. */
  redirectTo: (mode: CheckoutMode) => void;
  skipConfirmation: boolean;
  onCompleted?: (order: Order) => void;
  /** While the checkout is still landing on its entry step, `useCheckoutBootstrap` decides. */
  bootstrapping?: boolean;
};

export const useCheckoutModeDecision = (params: UseCheckoutModeDecisionParams) => {
  const { mode, redirectTo, skipConfirmation, onCompleted, bootstrapping = false } = params;

  const dispatch = useAppDispatch();
  const [, logged] = useAppSelector(buyerSelector);
  const order = useAppSelector(currentOrderSelector);
  const sale = useAppSelector(currentSaleSelector);
  const saleChange = useAppSelector(currentSaleChangeSelector);
  const pendingFormInputs = useAppSelector(hasPendingSaleRequiredFormInputsSelector);

  const completedOrderId = useRef<string | null>(null);
  // The sale the waiting step handed on. It already exists, so there is no cart left to confirm.
  const confirmedSaleId = useRef<string | null>(null);

  const applyDecision = useCallback(
    (decision: CheckoutModeDecision | null) => {
      if (!decision) {
        return;
      }

      if (decision.appliedSaleChangeId) {
        dispatch(setShowAppliedFeedbackForSaleChange(decision.appliedSaleChangeId));
        dispatch(resetSaleChange());
      }

      if (order && decision.completedOrderId && decision.completedOrderId !== completedOrderId.current) {
        completedOrderId.current = decision.completedOrderId;
        onCompleted?.(order);
      }

      if (decision.mode !== mode) {
        redirectTo(decision.mode);
      }
    },
    [dispatch, redirectTo, mode, order, onCompleted],
  );

  useEffect(() => {
    if (bootstrapping) {
      return;
    }

    const purchase = { order, sale, saleChange, pendingFormInputs };
    const decision = decideCheckoutMode(purchase);

    // A confirmed sale has nothing left to decide from, but the waiting step must still hand it on —
    // straight to payment, or to the auth step first when a guest opened the link.
    if (!decision && mode === WAITING_CONFIRMATION_MODE && sale) {
      confirmedSaleId.current = sale.id;
      applyDecision(
        logged ? decideCheckoutModeAfterAuth({ ...purchase, skipConfirmation: true }) : { mode: AUTH_MODE },
      );
      return;
    }

    applyDecision(decision);
    // `pendingFormInputs` and `applyDecision` stay out of deps — legacy parity: only purchase changes re-decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [order, sale, saleChange, bootstrapping]);

  useEffect(() => {
    if (bootstrapping || !logged || mode !== AUTH_MODE) {
      return;
    }

    const saleConfirmed = sale != null && sale.id === confirmedSaleId.current;

    applyDecision(
      decideCheckoutModeAfterAuth({
        order,
        sale,
        saleChange,
        pendingFormInputs,
        skipConfirmation: skipConfirmation || saleConfirmed,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [logged, mode, bootstrapping]);
};

export default useCheckoutModeDecision;
